import classnames from 'classnames';
import type { ReactNode } from 'react';
import type { Vector2 } from '@sorrem/shared';
import type { SkillTreeNodeModel } from './skill-tree-model';
import { SKILL_TREE_LABEL_SPACE, SKILL_TREE_NODE_SIZE } from './skill-tree-layout';
import type { SkillTreeSvgIds } from './skill-tree-ids';
import { resolveSkillGroupTint, SKILL_TREE_CORE_FILL } from './skill-tree-palette';
import {
    ESSENCE_RING_CENTERS,
    MARTIAL_SEGMENT_PITCH,
    MARTIAL_SEGMENT_SWEEP,
    PIECE_BOX,
    PIECE_CENTER,
    resolveMartialSegmentCenter,
    resolveMartialSegmentStartAngle,
    toPieceUrl,
    buildWedgePath,
} from './skill-tree-pieces';
import { SKILL_TREE_SKILL_EMBLEMS } from './skill-tree-skill-emblems';
import { SkillTreeLineEmblem } from './SkillTreeLineEmblem';
import type { SkillTreeIntroSchedule } from './skill-tree-intro';
import { SKILL_TREE_NODE_INTROS, type SkillTreeNodeIntro, type SkillTreeNodePart } from './skill-tree-node-intros';
import { SkillTreeIntroPart } from './SkillTreeIntroPart';
import styles from './SkillTreeScene.module.scss';

interface SkillTreeNodeProps {
    readonly node: SkillTreeNodeModel;
    readonly isHovered: boolean;
    readonly isSelected: boolean;
    readonly isReducedMotion: boolean;
    /** The scene's SVG def ids (the label shadow, the emblem glow, the partial marker's clip). */
    readonly svgIds: SkillTreeSvgIds;
    /** The Skills tab intro while it plays: the node plays its type's entrance as its link arrives (or all of it in reverse). */
    readonly intro: SkillTreeIntroSchedule | undefined;
}

const HALF = SKILL_TREE_NODE_SIZE / 2;

/** A piece drawn in the node's 120-unit box (viewBox 0 0 120 120). */
function BoxPiece({ path, opacity, transform }: { readonly path: string; readonly opacity?: number; readonly transform?: string }): JSX.Element {
    return <image href={toPieceUrl(path)} width={PIECE_BOX} height={PIECE_BOX} opacity={opacity} transform={transform} />;
}

/** A piece whose viewBox extends past the node box by `margin` on every side (glows, borders). */
function WidePiece({ path, margin }: { readonly path: string; readonly margin: number }): JSX.Element {
    return <image href={toPieceUrl(path)} x={-margin} y={-margin} width={PIECE_BOX + margin * 2} height={PIECE_BOX + margin * 2} />;
}

/** The transform that turns the top Martial ring segment's piece into segment `index`. */
function martialSegmentRotation(index: number): string {
    return `rotate(${index * MARTIAL_SEGMENT_PITCH} ${PIECE_CENTER.x} ${PIECE_CENTER.y})`;
}

/** A node's intro while it plays: when its entrance starts, and its type's sequence. */
interface NodeEntrance {
    readonly start: number;
    readonly intro: SkillTreeNodeIntro;
}

/**
 * Wraps one part of a node in its entrance, timed by the node type's
 * sequence and posed by the intro's clock. `origin` is the point it scales
 * about, in the node box; `from` is where a grow-out starts, relative to
 * the part's place. Outside the intro (or for a part with no entrance) the
 * wrapper stays but sits idle, so the part's elements are never remounted
 * when the intro starts or ends.
 */
function Enter({ entrance, part, origin = PIECE_CENTER, from, children }: {
    readonly entrance: NodeEntrance | null;
    readonly part: SkillTreeNodePart;
    readonly origin?: Vector2;
    readonly from?: Vector2;
    readonly children: ReactNode;
}): JSX.Element {
    const step = entrance?.intro.step(part);
    const window = entrance && step ? { start: entrance.start + step.start, duration: step.duration } : undefined;
    return (
        <SkillTreeIntroPart window={window} motion={step?.effect ?? 'fade'} origin={origin} from={from}>
            {children}
        </SkillTreeIntroPart>
    );
}

/** From a point in the node box back to its center, for a part that grows out of the core. */
function fromCenter(point: Vector2): Vector2 {
    return { x: PIECE_CENTER.x - point.x, y: PIECE_CENTER.y - point.y };
}

function MartialMarkers({ node, clipId, entrance }: {
    readonly node: SkillTreeNodeModel;
    readonly clipId: string;
    readonly entrance: NodeEntrance | null;
}): JSX.Element {
    if (node.isMastered) {
        return (
            <Enter entrance={entrance} part={{ kind: 'mastered' }}>
                <WidePiece path="martial/mastered-ring.svg" margin={8} />
            </Enter>
        );
    }
    const suffix = node.isInactive ? '-inactive' : '';
    const partialStart = resolveMartialSegmentStartAngle(node.step);
    return (
        <>
            {Array.from({ length: node.maxStep }, (_, index) => (
                <Enter key={index} entrance={entrance} part={{ kind: 'marker', index }} origin={resolveMartialSegmentCenter(index)}>
                    <BoxPiece
                        path={`martial/segment-${index < node.step ? 'filled' : 'empty'}${suffix}.svg`}
                        transform={martialSegmentRotation(index)}
                    />
                </Enter>
            ))}
            {node.progress > 0 && (
                <Enter entrance={entrance} part={{ kind: 'marker', index: node.step }} origin={resolveMartialSegmentCenter(node.step)}>
                    <clipPath id={clipId}>
                        <path d={buildWedgePath(PIECE_CENTER, PIECE_BOX / 2, partialStart, partialStart + MARTIAL_SEGMENT_SWEEP * node.progress)} />
                    </clipPath>
                    <g clipPath={`url(#${clipId})`}>
                        <BoxPiece path={`martial/segment-filled${suffix}.svg`} transform={martialSegmentRotation(node.step)} />
                    </g>
                </Enter>
            )}
        </>
    );
}

function EssenceMarkers({ node, clipId, entrance }: {
    readonly node: SkillTreeNodeModel;
    readonly clipId: string;
    readonly entrance: NodeEntrance | null;
}): JSX.Element {
    if (node.isMastered) {
        return (
            <>
                <Enter entrance={entrance} part={{ kind: 'mastered' }}>
                    <BoxPiece path="essence/spiral-blaze.svg" />
                </Enter>
                {ESSENCE_RING_CENTERS.map((center, index) => (
                    <Enter key={index} entrance={entrance} part={{ kind: 'marker', index }} origin={center} from={fromCenter(center)}>
                        <image href={toPieceUrl('essence/ring-diamond.svg')} x={center.x - 12} y={center.y - 12} width={24} height={24} />
                    </Enter>
                ))}
            </>
        );
    }
    const filling = node.progress > 0 ? node.step : -1;
    return (
        <>
            {/* The stretch of spiral leading into a ring lights once the ring is reached or filling. */}
            {ESSENCE_RING_CENTERS.map((_, index) => (
                <Enter key={index} entrance={entrance} part={{ kind: 'path', index }}>
                    <BoxPiece path={`essence/spiral-${index + 1}-${index < node.step || index === filling ? 'lit' : 'dim'}.svg`} />
                </Enter>
            ))}
            {ESSENCE_RING_CENTERS.map((center, index) => (
                <Enter key={index} entrance={entrance} part={{ kind: 'marker', index }} origin={center} from={fromCenter(center)}>
                    <image
                        href={toPieceUrl(`essence/ring-${index < node.step ? 'lit' : 'dim'}.svg`)}
                        x={center.x - 10}
                        y={center.y - 10}
                        width={20}
                        height={20}
                    />
                </Enter>
            ))}
            {filling >= 0 && ESSENCE_RING_CENTERS[filling] && (() => {
                const center = ESSENCE_RING_CENTERS[filling]!;
                const fillHeight = 13 * node.progress;
                return (
                    <Enter entrance={entrance} part={{ kind: 'marker', index: filling }} origin={center} from={fromCenter(center)}>
                        {/* Light rises inside the next ring like liquid. */}
                        <clipPath id={clipId}>
                            <rect x={center.x - 6.5} y={center.y + 6.5 - fillHeight} width={13} height={fillHeight} />
                        </clipPath>
                        <image href={toPieceUrl('essence/ring-fill.svg')} x={center.x - 10} y={center.y - 10} width={20} height={20} clipPath={`url(#${clipId})`} />
                    </Enter>
                );
            })()}
        </>
    );
}

/** How large a skill's line emblem is drawn in its core, per branch: Martial and Essence cores are the same size, so their emblems are too. */
const EMBLEM_SCALE = { martial: 1.2, essence: 1.2, crafting: 0.9 } as const;

/** A node's branch body: its progress markers and its core, under the emblem. */
function NodeBody({ node, clipId, entrance }: {
    readonly node: SkillTreeNodeModel;
    readonly clipId: string;
    readonly entrance: NodeEntrance | null;
}): JSX.Element | null {
    const { branch } = node.placement;
    switch (branch) {
        case 'martial':
            return (
                <>
                    <Enter entrance={entrance} part={{ kind: 'glow' }}>
                        <BoxPiece path="martial/glow.svg" opacity={0.08 + 0.04 * node.step} />
                    </Enter>
                    <MartialMarkers node={node} clipId={clipId} entrance={entrance} />
                    <Enter entrance={entrance} part={{ kind: 'core' }}>
                        <BoxPiece path="martial/core.svg" />
                    </Enter>
                </>
            );
        case 'essence':
            return (
                <>
                    <EssenceMarkers node={node} clipId={clipId} entrance={entrance} />
                    <Enter entrance={entrance} part={{ kind: 'core' }}>
                        <BoxPiece path="essence/core.svg" />
                    </Enter>
                </>
            );
        case 'crafting':
            // Crafting has no awakened look yet; its unawakened nodes draw a cross instead (see SkillTreeNode).
            return null;
        default: {
            const unknownBranch: never = branch;
            return unknownBranch;
        }
    }
}

/**
 * One skill on the tree, assembled from the branch's SVG pieces in its
 * 120-unit node box and placed at its tree position. During the Skills tab
 * intro each part enters on its node type's sequence
 * (`skill-tree-node-intros.ts`) once the link from its dial arrives. The
 * transparent hit circle carries `data-node-id`, which `SkillTree` reads to
 * hover and select.
 */
export function SkillTreeNode({ node, isHovered, isSelected, isReducedMotion, svgIds, intro }: SkillTreeNodeProps): JSX.Element {
    const { placement } = node;
    const origin = { x: placement.position.x - HALF, y: placement.position.y - HALF };
    const clipId = svgIds.nodeClip(placement.id);
    const emblem = SKILL_TREE_SKILL_EMBLEMS[placement.id];
    const emblemColor = resolveSkillGroupTint(placement.group);
    const decaying = node.decayRisk === 'moderate' || node.decayRisk === 'high';
    const entranceWindow = intro?.nodes.get(placement.id)?.entrance;
    const entrance: NodeEntrance | null = entranceWindow
        ? { start: entranceWindow.start, intro: SKILL_TREE_NODE_INTROS[placement.branch] }
        : null;

    const label = (
        <Enter entrance={entrance} part={{ kind: 'label' }}>
            <text
                className={classnames(styles.nodeLabel, node.isUnawakened && styles.unawakenedLabel)}
                x={placement.position.x}
                y={placement.position.y + HALF + SKILL_TREE_LABEL_SPACE * 0.6}
                filter={`url(#${svgIds.textShadow})`}
            >
                {node.name}
            </text>
        </Enter>
    );
    const hitTarget = (
        <circle className={styles.hitTarget} cx={placement.position.x} cy={placement.position.y} r={HALF} data-node-id={placement.id} />
    );

    if (node.isUnawakened) {
        // A branch that hasn't awakened: a faint cross with its name, and nothing to fill.
        return (
            <g>
                <g transform={`translate(${origin.x} ${origin.y})`}>
                    <Enter entrance={entrance} part={{ kind: 'core' }}>
                        <BoxPiece path="crafting/unawakened-cross.svg" />
                    </Enter>
                    {isHovered && !isSelected && <WidePiece path={`${placement.branch}/halo-hover.svg`} margin={4} />}
                    {isSelected && <WidePiece path={`${placement.branch}/rings-selected.svg`} margin={6} />}
                </g>
                {label}
                {hitTarget}
            </g>
        );
    }

    return (
        <g
            className={classnames(
                styles.node,
                node.isInactive && styles.inactive,
                decaying && !isReducedMotion && (node.decayRisk === 'high' ? styles.decayHigh : styles.decayModerate),
            )}
        >
            <g transform={`translate(${origin.x} ${origin.y})`}>
                <NodeBody node={node} clipId={clipId} entrance={entrance} />
                {emblem && (
                    <Enter entrance={entrance} part={{ kind: 'icon' }}>
                        <g transform={`translate(${PIECE_CENTER.x} ${PIECE_CENTER.y})`}>
                            <SkillTreeLineEmblem
                                layers={emblem}
                                color={emblemColor}
                                fill={SKILL_TREE_CORE_FILL[placement.branch]}
                                scale={EMBLEM_SCALE[placement.branch]}
                                glowFilterId={svgIds.emblemGlow}
                            />
                        </g>
                    </Enter>
                )}
                {/* State overlays arrive with the label. */}
                <Enter entrance={entrance} part={{ kind: 'label' }}>
                    {decaying && isReducedMotion && <WidePiece path={`${placement.branch}/halo-decay-static.svg`} margin={4} />}
                    {isHovered && !isSelected && <WidePiece path={`${placement.branch}/halo-hover.svg`} margin={4} />}
                    {isSelected && <WidePiece path={`${placement.branch}/rings-selected.svg`} margin={6} />}
                </Enter>
            </g>
            {label}
            {hitTarget}
        </g>
    );
}
